import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type UserRole = 'student' | 'teacher' | 'admin';
export type Permission = 
  | 'project.create'
  | 'project.edit'
  | 'project.delete'
  | 'project.share'
  | 'module.install'
  | 'module.publish'
  | 'simulation.run'
  | 'robot.control'
  | 'admin.access';

export interface User {
  id: string;
  email: string;
  username: string;
  fullName?: string;
  role: UserRole;
  permissions: Permission[];
  avatarUrl?: string;
  tenantId?: string;
  isActive: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setRefreshToken: (state, action: PayloadAction<string>) => {
      state.refreshToken = action.payload;
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; token: string; refreshToken: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setLoading,
  setUser,
  setToken,
  setRefreshToken,
  loginSuccess,
  loginFailure,
  logout,
  updateUser,
  setError,
} = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectUserRole = (state: { auth: AuthState }) => state.auth.user?.role;
export const selectUserPermissions = (state: { auth: AuthState }) => state.auth.user?.permissions || [];
export const selectHasPermission = (permission: Permission) => (state: { auth: AuthState }) =>
  state.auth.user?.permissions.includes(permission) || state.auth.user?.role === 'admin';
export const selectHasRole = (role: UserRole) => (state: { auth: AuthState }) =>
  state.auth.user?.role === role || state.auth.user?.role === 'admin';

